'use client';

import * as React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cn } from '@packages/shared/src/utils/cn/cn';
import type { CardProps } from '@packages/shared/src/ui/Card/Card';
import { Card } from '@packages/shared/src/ui/Card/Card';
import { Button } from '@packages/shared/src/ui/Button/Button';

export interface SelectableCardProps extends CardProps {
  isActive?: boolean;
}

export interface CardTriggerProps {
  className?: string;
  isActive?: boolean;
  onSelect?: () => void;
}

export interface CardTriggerContentProps extends CardTriggerProps {
  children: React.ReactNode;
}

const messages = defineMessages({
  defaultTrigger: {
    id: 'SelectableCard.defaultTrigger',
    defaultMessage: 'wählen',
  },
  selected: {
    id: 'SelectableCard.selected',
    defaultMessage: 'ausgewählt',
  },
});

export const DefaultCardTrigger: React.FC<CardTriggerProps> = ({
  className,
  isActive,
  onSelect,
}) => {
  const { formatMessage } = useIntl();

  return (
    <Button
      className={cn('w-[7.5rem]', className)}
      color={isActive ? 'primary' : undefined}
      onClick={onSelect}
      aria-label={
        isActive ? formatMessage(messages.selected) : formatMessage(messages.defaultTrigger)
      }
    >
      {isActive ? (
        <span className="icon-emp-checkmark text-[1.5rem]" aria-hidden="true" />
      ) : (
        formatMessage(messages.defaultTrigger)
      )}
    </Button>
  );
};

export const DefaultTriggerRow: React.FC<CardTriggerContentProps> = ({
  children,
  className,
  isActive,
  onSelect,
}) => (
  <div className={cn('flex items-center justify-between', className)}>
    {children}
    <DefaultCardTrigger className="ml-auto break-normal" isActive={isActive} onSelect={onSelect} />
  </div>
);

export const SelectableCard = React.forwardRef<HTMLDivElement, SelectableCardProps>(
  ({ className, children, isActive, onSelect, ...props }, ref) => (
    <Card
      className={cn('transition-all', isActive && 'border-color-primary border-l-4', className)}
      ref={ref}
      {...props}
    >
      {children}
    </Card>
  ),
);
SelectableCard.displayName = 'SelectableCard';
